// Enhanced CrmDashboards.tsx with Global Search and Pagination
import React, { useState, useCallback, useMemo, useEffect } from "react";
import PatientProfilePopup from "../DoctorDashboard/PatientProfile";
import { TiArrowRight } from "react-icons/ti";
import PaginationControls from "./PaginationControls";
import "../ScrollBar/ScrollBar.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { decryptData } from "../Services/TabServices";

interface CrmDashboardProps {
  page: string;
  title: string;
  subtitle: string;
  noDataMessage: string;
  appointments: any[];
  allAppointments: any[];
  filters: {
    speciality: string;
    doctor: string;
    date: string;
    priority: string;
  };
  setFilters: React.Dispatch<
    React.SetStateAction<{
      speciality: string;
      doctor: string;
      date: string;
      priority: string;
    }>
  >;
  setShowProfilePopup: React.Dispatch<React.SetStateAction<boolean>>;
  specialities: string[];
  currentPage: number;
  totalPages: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  itemsPerPage: number;
  setItemsPerPage: React.Dispatch<React.SetStateAction<number>>;
}

export const CrmDashboards: React.FC<CrmDashboardProps> = ({
  page,
  title,
  subtitle,
  noDataMessage,
  allAppointments,
  filters,
  setFilters,
  specialities,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
}) => {
  const storedUserId = sessionStorage.getItem("userId");
   const CrmID = storedUserId ? decryptData(storedUserId) : null;
  const [patientId, setPatientId] = useState<string | null>(null);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [searchPatient, setSearchPatient] = useState("");
  const [searchDoctor, setSearchDoctor] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [searchOldDoctor, setSearchOldDoctor] = useState("");
  const [searchOldDate, setSearchOldDate] = useState("");
  const [searchNewDoctor, setSearchNewDoctor] = useState("");
  const [searchNewDate, setSearchNewDate] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const globalFilteredAppointments = useMemo(() => {
    let filtered = allAppointments;

    if (filters.speciality) {
      filtered = filtered.filter((appointment) =>
        appointment.specializationName?.toLowerCase().includes(filters.speciality.toLowerCase())
      );
    }

    if (page !== "Rescheduled") {
      filtered = filtered.filter((appointment) => {
        return (
          appointment.patientName
            ?.toLowerCase()
            .includes(searchPatient.toLowerCase()) &&
          appointment.doctorName
            ?.toLowerCase()
            .includes(searchDoctor.toLowerCase()) &&
          appointment.formattedDate
            ?.toLowerCase()
            .includes(searchDate.toLowerCase())
        );
      });
    } else {
      filtered = filtered.filter((appointment) => {
        return (
          appointment.patientName
            ?.toLowerCase()
            .includes(searchPatient.toLowerCase()) &&
          appointment.doctorName
            ?.toLowerCase()
            .includes(searchOldDoctor.toLowerCase()) &&
          appointment.formattedDate
            ?.toLowerCase()
            .includes(searchOldDate.toLowerCase()) &&
          appointment.newDoctorName
            ?.toLowerCase()
            .includes(searchNewDoctor.toLowerCase()) &&
          appointment.newformattedDate
            ?.toLowerCase()
            .includes(searchNewDate.toLowerCase())
        );
      });
    }

    return filtered;
  }, [
    allAppointments,
    filters.speciality,
    searchPatient,
    searchDoctor,
    searchDate,
    searchOldDoctor,
    searchOldDate,
    searchNewDoctor,
    searchNewDate,
    page
  ]);

  const totalPages = useMemo(() => {
    return Math.ceil(globalFilteredAppointments.length / itemsPerPage);
  }, [globalFilteredAppointments.length, itemsPerPage]);

  const paginatedAppointments = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return globalFilteredAppointments.slice(startIndex, endIndex);
  }, [globalFilteredAppointments, currentPage, itemsPerPage]);

  // 🚀 Reset to page 1 when search changes and current page becomes invalid
useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages, setCurrentPage]);

const formatExportDate = (dateStr: string) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()); // last 2 digits
  return `${day}/${month}/${year}`;
};


  const exportToExcel = useCallback(() => {
    if (globalFilteredAppointments.length === 0) {
      alert("No data to export!");
      return;
    }

    setIsExporting(true);

    try {
      let exportData: any[] = [];

      if (page === "Rescheduled") {
        exportData = globalFilteredAppointments.map((appointment, index) => ({
          "Sr. No.": index + 1,
          "Appointment ID": appointment.appointmentId,
          "Crm ID":CrmID,
          "Patient Name": appointment.patientName,
          "Age": appointment.age,
          "Gender": appointment.gender,
          "Old Doctor": appointment.doctorName,
          "Old Specialization": appointment.specializationName,
          "Old Date": `${formatExportDate(appointment.appointmentDate)}`,
          "Old Time": appointment.formattedTime,
          "New Doctor": appointment.newDoctorName,
          "New Specialization": appointment.specialization_NameNew,
          "New Date": `${formatExportDate(appointment.newformattedDate)}`,
          "New Time": appointment.newformattedTime,
          "Appointment Status": "Rescheduled",
          "Export Date": new Date().toLocaleDateString(),
          "Export Time": new Date().toLocaleTimeString(),
        }));
      } else {
        exportData = globalFilteredAppointments.map((appointment, index) => ({
          "Sr. No.": index + 1,
          "Appointment ID": appointment.appointmentId,
          "Crm ID":CrmID,
          "Patient Name": appointment.patientName,
          "Age": appointment.age,
          "Gender": appointment.gender,
          "Doctor Name": appointment.doctorName,
          "Specialization": appointment.specializationName,
          "Appointment Date": `${formatExportDate(appointment.appointmentDate)}`,
          "Appointment Time": appointment.formattedTime,
          "Appointment Status": appointment.status || page,
          "Export Date": new Date().toLocaleDateString(),
          "Export Time": new Date().toLocaleTimeString(),
        }));
      }

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      const columnWidths = [
        { wch: 8 }, { wch: 15 }, { wch: 8 },{ wch: 20 }, { wch: 8 }, { wch: 10 },
        { wch: 15 }, { wch: 20 }, { wch: 25 }, { wch: 15 }, { wch: 12 },
        { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 12 },
      ];
      
      worksheet["!cols"] = columnWidths;
      XLSX.utils.book_append_sheet(workbook, worksheet, page === "Rescheduled" ? "Rescheduled Appointments" : `${page} Appointments`);

      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `${page.replace(/\s+/g, "_")}_Appointments_${timestamp}.xlsx`;
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: "application/octet-stream" });
      saveAs(data, filename);      
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert("❌ Error exporting to Excel. Please try again.");
    } finally {
      setIsExporting(false);
    }
  }, [globalFilteredAppointments, page]);

  const handleSearchChange = useCallback((field: string, value: string) => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    switch (field) {
      case 'patient':
        setSearchPatient(value);
        break;
      case 'doctor':
        setSearchDoctor(value);
        break;
      case 'date':
        setSearchDate(value);
        break;
      case 'oldDoctor':
        setSearchOldDoctor(value);
        break;
      case 'oldDate':
        setSearchOldDate(value);
        break;
      case 'newDoctor':
        setSearchNewDoctor(value);
        break;
      case 'newDate':
        setSearchNewDate(value);
        break;
    }
  }, [currentPage, setCurrentPage]);

  return (
    <div className="ml-2">
      {showProfilePopup && (
        <PatientProfilePopup
          patientId={patientId !== null ? Number(patientId) : null}
          onClose={() => setShowProfilePopup(false)}
        />
      )}
      <div className="w-full">
        <div className="sticky top-[128px] sm:top-[140px] w-full bg-[#F5F5F5] pb-3 z-10">
          <div className="flex justify-between">
            <div>
              <p className="text-[16px] font-semibold w-full">{title}</p>
              <p className="text-[12px] text-[#606060] w-full">
                {subtitle}
                {globalFilteredAppointments.length !== allAppointments.length && (
                  <span className="ml-2 text-[#184A84] font-semibold">
                    ({globalFilteredAppointments.length} of {allAppointments.length} appointments)
                  </span>
                )}
              </p>
            </div>
            <div className="flex justify-around items-center w-[40%] hidden sm:flex">
              <div className="flex justify-center gap-3">
                {/*  Export Button */}
                <button
                  onClick={exportToExcel}
                  disabled={isExporting || globalFilteredAppointments.length === 0}
                  className={`flex items-center px-2 h-[25px] mt-2.5 rounded transition-colors ${
                    isExporting || globalFilteredAppointments.length === 0
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-blue-100 hover:bg-blue-100 text-gray-700"
                  }`}
                  title={`Export ${globalFilteredAppointments.length} appointments to Excel`}
                >
                  {isExporting ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                  <span className="ml-1 text-xs">Export</span>
                </button>

                <div className="relative inline-block mt-2">
                  <select
                    onChange={(e) => {
                      setFilters((prev) => ({
                        ...prev,
                        speciality: e.target.value,
                      }));
                      if (currentPage !== 1) setCurrentPage(1); 
                    }}
                    value={filters.speciality || ""}
                    className="w-[150px] py-1 px-4 pr-8 rounded-md text-[12px] text-[#009A85] border-none appearance-none shadow-sm shadow-gray-400 focus:outline-none focus:ring-0"
                    title="Select speciality"
                  >
                    <option className="text-black" value="">
                      All Specialities
                    </option>
                    {specialities.map((speciality: any) => (
                      <option key={speciality.specialization} value={speciality.specialization}>
                        {speciality.specialization}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-[#009A85] text-sm">
                    ▾
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Export Button */}
          <div className="flex sm:hidden mt-3 justify-end">
            <button
              onClick={exportToExcel}
              disabled={isExporting || globalFilteredAppointments.length === 0}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
                isExporting || globalFilteredAppointments.length === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 text-white"
              }`}
            >
              {isExporting ? (
                <>
                  <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Exporting...
                </>
              ) : (
                <>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export ({globalFilteredAppointments.length})
                </>
              )}
            </button>
          </div>
        </div>

        <div
          className="w-[98%] rounded-md mt-12 flex-1 overflow-y-auto custom-scrollbar"
          style={{ height: "calc(100vh - 216px)" }}
        >
          {page !== "Rescheduled" ? (
            <div className="bg-[#F5F5F5]">
              <div className="hidden sm:grid grid-cols-4 gap-2 mt-6 px-2 xl:pl-10 text-xs font-semibold text-gray-600 tracking-wider">
                <div className="border-r">Patient Info</div>
                <div className="border-r xl:pl-2">Doctor Info</div>
                <div className="xl:pl-2">Date & Time</div>
                <div className="xl:pl-20">Action</div>
              </div>
              <div className="hidden sm:grid grid-cols-4 gap-2 mt-1 px-2 xl:pl-10 text-xs text-gray-600 tracking-wider">
                <input
                  placeholder="Search Patient"
                  className="text-xs px-3 py-1 rounded-md border border-gray-300 shadow-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  value={searchPatient}
                  onChange={(e) => handleSearchChange('patient', e.target.value)}
                />
                <input
                  placeholder="Search Doctor"
                  className="text-xs px-3 py-1 rounded-md border border-gray-300 shadow-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  value={searchDoctor}
                  onChange={(e) => handleSearchChange('doctor', e.target.value)}
                />
                <input
                  placeholder="Search Date"
                  className="text-xs px-3 py-1 rounded-md border border-gray-300 shadow-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  value={searchDate}
                  onChange={(e) => handleSearchChange('date', e.target.value)}
                />
                <div></div>
              </div>
            </div>
          ) : (
            <>
              <div className="hidden sm:grid grid-cols-6 gap-2 mt-6 px-2 xl:pl-10 text-xs font-semibold text-gray-600 tracking-wider">
                <div className="border-r">Patient Info</div>
                <div className="border-r">Old Doctor</div>
                <div>Old Date</div>
                <div className="border-l border-r pl-2">New Doctor</div>
                <div>New Date</div>
                <div className="pl-5">Action</div>
              </div>
              <div className="hidden sm:grid grid-cols-6 gap-2 mt-1 px-2 xl:pl-10 text-xs text-gray-600 tracking-wider">
                <input
                  placeholder="Search Patient"
                  className="text-xs px-3 py-1 rounded-md border border-gray-300 shadow-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  value={searchPatient}
                  onChange={(e) => handleSearchChange('patient', e.target.value)}
                />
                <input
                  placeholder="Old Doctor"
                  className="text-xs px-3 py-1 rounded-md border border-gray-300 shadow-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  value={searchOldDoctor}
                  onChange={(e) => handleSearchChange('oldDoctor', e.target.value)}
                />
                <input
                  placeholder="Old Date"
                  className="text-xs px-3 py-1 rounded-md border border-gray-300 shadow-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  value={searchOldDate}
                  onChange={(e) => handleSearchChange('oldDate', e.target.value)}
                />
                <input
                  placeholder="New Doctor"
                  className="text-xs px-3 py-1 rounded-md border border-gray-300 shadow-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  value={searchNewDoctor}
                  onChange={(e) => handleSearchChange('newDoctor', e.target.value)}
                />
                <input
                  placeholder="New Date"
                  className="text-xs px-3 py-1 rounded-md border border-gray-300 shadow-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  value={searchNewDate}
                  onChange={(e) => handleSearchChange('newDate', e.target.value)}
                />
                <div></div>
              </div>
            </>
          )}

          {paginatedAppointments.length === 0 ? (
            <div className="text-center bg-white py-8 rounded-lg mt-2 font-semibold">
              {globalFilteredAppointments.length === 0 ? noDataMessage : "No appointments found on this page."}
            </div>
          ) : (
            paginatedAppointments.map((appointment: any, index: number) => (
              <div
                key={`${appointment.appointmentId}-${index}`}
                className={`grid grid-cols-2 ${
                  page === "Rescheduled" ? "sm:grid-cols-6" : "sm:grid-cols-4"
                } gap-2 py-2 items-center bg-white p-2 mt-2 xl:pl-10 rounded-xl border border-transparent hover:bg-[#D6E0F0] hover:border-[#B0C4DE] transition`}
              >
                <div className="border-r">
                  <p className="text-[14px] font-semibold">{appointment.patientName}</p>
                  <p className="text-xs text-gray-500">
                    Appointment ID: {appointment.appointmentId}, ({appointment.age})yrs, {appointment.gender}
                  </p>
                </div>

                <div className="border-r xl:pl-2">
                  <p className="text-[14px] font-semibold">{appointment.doctorName}</p>
                  <p className="text-xs text-gray-500">{appointment.specializationName}</p>
                </div>

                <div className="xl:pl-2 flex items-center justify-between gap-2">
                  <div>
                    <p className="text-[14px] text-[#184A84] font-semibold cursor-pointer">
                      {appointment.formattedDate}
                    </p>
                    <p className="text-xs text-[#184A84]">{appointment.formattedTime}</p>
                  </div>
                  {page === "Rescheduled" && (
                    <TiArrowRight className="text-[#184A84] text-xl mt-1 mr-4" />
                  )}
                </div>

                {page === "Rescheduled" && (
                  <>
                    <div className="border-l pl-2 border-r">
                      <p className="text-[14px] font-semibold">{appointment.newDoctorName}</p>
                      <p className="text-xs text-gray-500">{appointment.specialization_NameNew}</p>
                    </div>
                    <div className="">
                      <p className="text-[14px] text-[#184A84] font-semibold cursor-pointer">
                        {appointment.newformattedDate}
                      </p>
                      <p className="text-xs text-[#184A84]">{appointment.newformattedTime}</p>
                    </div>
                  </>
                )}

                <div
                  className={`flex items-center justify-between sm:justify-start gap-3 ${
                    page === "Rescheduled" ? "xl:pl-5" : "xl:pl-20"
                  }`}
                >
                  <button
                    className="bg-[#009A85] text-white text-xs font-semibold px-10 py-[5px] rounded-md hover:bg-[#038775]"
                    onClick={() => {
                      setShowProfilePopup(true);
                      setPatientId(appointment.patientId);
                    }}
                  >
                    Profile
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(newItemsPerPage) => {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
          }}
        />
      </div>
    </div>
  );
};
